import React, { useState } from 'react';
import { ChevronDown, Mail } from 'lucide-react';
import Button from '../components/Button';
import { useTranslations } from '../context/TranslationContext';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQPage: React.FC = () => {
  const { t } = useTranslations();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: { [key: string]: FAQItem[] } = {
    general: [
      { question: t('faq_general_q1'), answer: t('faq_general_a1') },
      { question: t('faq_general_q2'), answer: t('faq_general_a2') },
      { question: t('faq_general_q3'), answer: t('faq_general_a3') },
    ],
    product: [
      { question: t('faq_new_product_q1'), answer: t('faq_new_product_a1') },
      { question: t('faq_new_product_q2'), answer: t('faq_new_product_a2') },
      { question: t('faq_new_product_q3'), answer: t('faq_new_product_a3') },
      { question: t('faq_new_product_q4'), answer: t('faq_new_product_a4') },
      { question: t('faq_new_integration_q1'), answer: t('faq_new_integration_a1') },
    ],
    pricing: [
      { question: t('faq_pricing_q1'), answer: t('faq_pricing_a1') },
      { question: t('faq_pricing_q2'), answer: t('faq_pricing_a2') },
      { question: t('faq_pricing_q3'), answer: t('faq_pricing_a3') },
    ],
    security: [
      { question: t('faq_security_q1'), answer: t('faq_security_a1') },
      { question: t('faq_security_q3'), answer: t('faq_security_a3') },
      { question: t('faq_security_q4'), answer: t('faq_security_a4') },
    ]
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="font-sans antialiased overflow-x-hidden text-white bg-void-blue">
      {/* Hero Section */}
      <section id="hero" className="relative flex items-center pt-32 pb-12 bg-void-blue">
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12">
          <div className="max-w-5xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl lg:text-5.5xl font-bold mb-6 leading-tight text-white">
              {t('faq_page_title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto">
              {t('faq_page_subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* General FAQs */}
      <section id="general" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Title - Left */}
            <div className="lg:col-span-1">
              <h2 className="font-display text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('faq_section_general')}
              </h2>
            </div>
            {/* Questions - Right */}
            <div className="lg:col-span-3 space-y-4">
              {faqs.general.map((faq, index) => (
                <div key={index} className="glass-panel rounded-xl overflow-hidden border border-white/10">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                    <ChevronDown
                      className={`flex-shrink-0 w-6 h-6 text-mystic-violet transition-transform duration-200 ${openIndex === index ? 'transform rotate-180' : ''
                        }`}
                    />
                  </button>
                  {openIndex === index && (
                    <div className="px-6 pb-5 text-gray-300 leading-relaxed border-t border-white/5 pt-4">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Product FAQs */}
      <section id="product" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Title - Left */}
            <div className="lg:col-span-1">
              <h2 className="font-display text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('faq_section_product')}
              </h2>
            </div>
            {/* Questions - Right */}
            <div className="lg:col-span-3 space-y-4">
              {faqs.product.map((faq, index) => {
                const faqIndex = faqs.general.length + index;
                return (
                  <div key={faqIndex} className="glass-panel rounded-xl overflow-hidden border border-white/10">
                    <button
                      onClick={() => toggleFAQ(faqIndex)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                      <ChevronDown
                        className={`flex-shrink-0 w-6 h-6 text-mystic-violet transition-transform duration-200 ${openIndex === faqIndex ? 'transform rotate-180' : ''
                          }`}
                      />
                    </button>
                    {openIndex === faqIndex && (
                      <div className="px-6 pb-5 text-gray-300 leading-relaxed border-t border-white/5 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing FAQs */}
      <section id="pricing" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Title - Left */}
            <div className="lg:col-span-1">
              <h2 className="font-display text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('faq_section_pricing')}
              </h2>
            </div>
            {/* Questions - Right */}
            <div className="lg:col-span-3 space-y-4">
              {faqs.pricing.map((faq, index) => {
                const faqIndex = faqs.general.length + faqs.product.length + index;
                return (
                  <div key={faqIndex} className="glass-panel rounded-xl overflow-hidden border border-white/10">
                    <button
                      onClick={() => toggleFAQ(faqIndex)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                      <ChevronDown
                        className={`flex-shrink-0 w-6 h-6 text-mystic-violet transition-transform duration-200 ${openIndex === faqIndex ? 'transform rotate-180' : ''
                          }`}
                      />
                    </button>
                    {openIndex === faqIndex && (
                      <div className="px-6 pb-5 text-gray-300 leading-relaxed border-t border-white/5 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Security FAQs */}
      <section id="security" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Section Title - Left */}
            <div className="lg:col-span-1">
              <h2 className="font-display text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('faq_section_security')}
              </h2>
            </div>
            {/* Questions - Right */}
            <div className="lg:col-span-3 space-y-4">
              {faqs.security.map((faq, index) => {
                const faqIndex = faqs.general.length + faqs.product.length + faqs.pricing.length + index;
                return (
                  <div key={faqIndex} className="glass-panel rounded-xl overflow-hidden border border-white/10">
                    <button
                      onClick={() => toggleFAQ(faqIndex)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                    >
                      <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                      <ChevronDown
                        className={`flex-shrink-0 w-6 h-6 text-mystic-violet transition-transform duration-200 ${openIndex === faqIndex ? 'transform rotate-180' : ''
                          }`}
                      />
                    </button>
                    {openIndex === faqIndex && (
                      <div className="px-6 pb-5 text-gray-300 leading-relaxed border-t border-white/5 pt-4">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-16 z-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-void-blue to-deep-space z-0"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl bg-gradient-radial from-cosmic-teal/10 to-transparent opacity-50 blur-3xl pointer-events-none"></div>

        <div className="container mx-auto px-6 md:px-12 text-center max-w-4xl relative z-10">
          <h2 className="font-display text-4xl md:text-5xl font-bold mb-8 text-white">
            {t('solutions_cta_title')}
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-text-secondary">
            {t('solutions_cta_subtitle')}
          </p>
          <p className="text-lg mb-12 text-text-muted">
            {t('solutions_cta_description')}
          </p>

          <Button
            size="lg"
            variant="gradient"
            icon={<Mail size={20} />}
            onClick={() => {
              const subject = encodeURIComponent(t("demo_email_subject"));
              const body = encodeURIComponent(t("demo_email_body"));
              window.location.href = `mailto:info@legisratio.it?subject=${subject}&body=${body}`;
            }}
            className="w-full sm:w-auto shadow-glow-teal hover:shadow-glow-teal-lg transition-shadow duration-300"
          >
            {t('solutions_book_demo')}
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
